import { useEffect, useRef, useState } from "react";
import { m as motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";
import { GrainOverlay } from "./GrainOverlay";

interface Props {
  /** Crossfading background images (cheap, no YT bandwidth). */
  images: string[];
  poster?: string;
  className?: string;
  /** Crossfade interval in ms */
  interval?: number;
  /** Gradient direction overlay */
  tone?: "mint" | "butter" | "noir";
}

const toneOverlay: Record<NonNullable<Props["tone"]>, string> = {
  mint: "bg-[radial-gradient(ellipse_at_30%_40%,color-mix(in_oklab,var(--mint)_18%,transparent)_0%,transparent_55%)]",
  butter: "bg-[radial-gradient(ellipse_at_70%_30%,color-mix(in_oklab,var(--butter)_18%,transparent)_0%,transparent_55%)]",
  noir: "bg-[radial-gradient(ellipse_at_50%_50%,rgba(255,255,255,0.04)_0%,transparent_60%)]",
};

export function HeroVideoLoop({ images, className, interval = 4500, tone = "mint" }: Props) {
  const [idx, setIdx] = useState(0);
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  useEffect(() => {
    if (reduce || images.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), interval);
    return () => clearInterval(t);
  }, [images.length, interval, reduce]);

  return (
    <div ref={ref} className={cn("absolute inset-0 -z-10 overflow-hidden", className)}>
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        {images.map((src, i) => (
          <motion.img
            key={src}
            src={src}
            alt="Stillbild ur filmproduktion av Fenny"
            aria-hidden="true"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{
              opacity: i === idx ? 0.55 : 0,
              scale: i === idx ? 1.02 : 1.08,
            }}
            transition={{ opacity: { duration: 1.4, ease: "easeOut" }, scale: { duration: 8, ease: "linear" } }}
            className="absolute inset-0 h-full w-full object-cover object-center"
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "auto"}
          />
        ))}
      </motion.div>
      {/* Cinematic gradient layers for legibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      <div className={cn("absolute inset-0", toneOverlay[tone])} />
      <GrainOverlay opacity={0.08} />
    </div>
  );
}
